import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Save, Eye, Pencil, Home } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import ConfirmActionDialog from "@/components/ConfirmActionDialog";
import ProofImageGallery from "@/components/ProofImageGallery";
import ProofImageDropzone from "@/components/ProofImageDropzone";
import { PageHero, PageShell } from "@/components/PageShell";
import { InlineHint, PageLoadingState } from "@/components/PageStates";
import { getTodayInBusinessTz } from "@/lib/date";
import { getFieldErrorClass, getFieldMessageClass } from "@/lib/form-ui";
import { handleArrowFieldKeyDown } from "@/lib/keyboardNav";
import { useNavigate } from "react-router-dom";
import {
  BARANG_KELUAR_ROLES,
  ROLE_KEPALA_RUANGAN,
} from "@/lib/roles";
import SearchableDropdown from "@/components/SearchableDropdown";
import { formatStockValue } from "@/lib/currency";
import {
  MAX_TRANSACTION_PROOF_IMAGES,
  MAX_TRANSACTION_PROOF_IMAGE_BYTES,
  PROOF_IMAGE_ACCEPT,
  buildProofImageSrc,
  createLocalProofImageEntries,
  isLikelyProofImageFile,
  getTransactionProofImageUrls,
} from "@/lib/proofImages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BARANG_KELUAR_DRAFT_KEY_PREFIX = "barang-keluar-draft";
const HISTORY_PAGE_SIZE = 10;

const getBarangUnits = (barang) => {
  const units = Object.keys(barang?.konversi_satuan || {});
  if (barang?.satuan && !units.includes(String(barang.satuan).toLowerCase())) {
    units.push(String(barang.satuan).toLowerCase());
  }
  return units;
};

const sumQtyByBarangId = (items = []) => {
  return items.reduce((acc, item) => {
    const barangId = item?.barang_id;
    if (!barangId) {
      return acc;
    }
    const qty = Number(item?.qty) || 0;
    acc[barangId] = (acc[barangId] || 0) + qty;
    return acc;
  }, {});
};

const createProofGalleryItems = ({ existingUrls = [], newEntries = [], onRemoveExisting, onRemoveNew }) => [
  ...existingUrls.map((url, index) => ({
    id: `existing-${url}-${index}`,
    src: buildProofImageSrc(url),
    href: buildProofImageSrc(url),
    alt: `Foto bukti ${index + 1}`,
    label: `Foto ${index + 1}`,
    onRemove:
      typeof onRemoveExisting === "function" ? () => onRemoveExisting(index) : undefined,
  })),
  ...newEntries.map((entry, index) => ({
    id: entry.id,
    src: entry.previewUrl,
    href: entry.previewUrl,
    alt: `Foto bukti baru ${index + 1}`,
    label: `Baru ${index + 1}`,
    onRemove: typeof onRemoveNew === "function" ? () => onRemoveNew(entry.id) : undefined,
  })),
];

const BarangKeluarPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [barangs, setBarangs] = useState([]);
  const [transaksi, setTransaksi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedTransaksi, setSelectedTransaksi] = useState(null);
  const [newFotoBuktiEntries, setNewFotoBuktiEntries] = useState([]);
  const [existingFotoUrls, setExistingFotoUrls] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getTodayInBusinessTz());
  const [searchBarang, setSearchBarang] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editingKeluarId, setEditingKeluarId] = useState(null);
  const [formData, setFormData] = useState({
    keterangan: "",
    items: [],
  });
  const [newItem, setNewItem] = useState({
    barang_id: "",
    qty: "",
    satuan_input: "",
    keterangan: "",
  });
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [editingItemDraft, setEditingItemDraft] = useState({
    qty_input: "",
    satuan_input: "",
    keterangan: "",
  });
  const [editBaselineQtyMap, setEditBaselineQtyMap] = useState({});
  const [draftReady, setDraftReady] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transaksiOffset, setTransaksiOffset] = useState(0);
  const [totalTransaksi, setTotalTransaksi] = useState(0);
  const [hasMoreTransaksi, setHasMoreTransaksi] = useState(false);
  const [loadingMoreTransaksi, setLoadingMoreTransaksi] = useState(false);
  const [historyDateDraft, setHistoryDateDraft] = useState({
    startDate: "",
    endDate: "",
  });
  const [historyDateFilter, setHistoryDateFilter] = useState({
    startDate: "",
    endDate: "",
  });
  const [historySearchTerm, setHistorySearchTerm] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [newItemErrors, setNewItemErrors] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    description: "",
    confirmText: "Ya",
    destructive: false,
    onConfirm: null,
  });
  const draftKey = `${BARANG_KELUAR_DRAFT_KEY_PREFIX}:${user?.username || "guest"}`;
  const canCreateBarangKeluar = BARANG_KELUAR_ROLES.includes(user?.role);
  const canViewHistory = BARANG_KELUAR_ROLES.includes(user?.role);
  const canManageHistory = user?.role === ROLE_KEPALA_RUANGAN;
  const barangInputRef = useRef(null);
  const qtyInputRef = useRef(null);
  const satuanSelectRef = useRef(null);
  const keteranganInputRef = useRef(null);
  const addItemButtonRef = useRef(null);
  const editQtyInputRef = useRef(null);
  const editSatuanSelectRef = useRef(null);
  const editKeteranganInputRef = useRef(null);
  const editSaveButtonRef = useRef(null);
  const transaksiFetchInFlightRef = useRef(false);
  const submitInFlightRef = useRef(false);

  const focusFieldNextFrame = useCallback((fieldRef) => {
    requestAnimationFrame(() => {
      fieldRef.current?.focus();
    });
  }, []);

  useEffect(() => {
    if (!user?.username) {
      setDraftReady(false);
      return;
    }

    setDraftReady(false);

    const rawDraft = localStorage.getItem(draftKey);
    if (!rawDraft) {
      setDraftReady(true);
      return;
    }

    try {
      const parsedDraft = JSON.parse(rawDraft);
      const draftFormData = parsedDraft?.formData;

      if (typeof parsedDraft?.selectedDate === "string") {
        setSelectedDate(parsedDraft.selectedDate);
      }

      if (draftFormData && typeof draftFormData === "object") {
        const restoredItems = Array.isArray(draftFormData.items)
          ? draftFormData.items
              .filter(
                (item) =>
                  item &&
                  typeof item.barang_id === "string" &&
                  item.barang_id.trim() !== "" &&
                  Number.isFinite(Number(item.qty)) &&
                  Number(item.qty) > 0
              )
              .map((item) => ({
                barang_id: item.barang_id,
                qty: Number(item.qty),
                qty_input: Number(item.qty_input ?? item.qty),
                satuan_input:
                  typeof item.satuan_input === "string" ? item.satuan_input : "",
                keterangan:
                  typeof item.keterangan === "string" ? item.keterangan : "",
              }))
          : [];

        setFormData({
          keterangan:
            typeof draftFormData.keterangan === "string"
              ? draftFormData.keterangan
              : "",
          items: restoredItems,
        });
      }
    } catch {
      localStorage.removeItem(draftKey);
    } finally {
      setDraftReady(true);
    }
  }, [draftKey, user?.username]);

  useEffect(() => {
    if (!user?.username || !draftReady || editMode) {
      return;
    }

    const draftPayload = {
      selectedDate,
      formData: {
        keterangan: formData.keterangan,
        items: formData.items,
      },
    };

    localStorage.setItem(draftKey, JSON.stringify(draftPayload));
  }, [
    draftKey,
    user?.username,
    draftReady,
    editMode,
    selectedDate,
    formData.keterangan,
    formData.items,
  ]);

  const removeExistingFotoAt = useCallback((indexToRemove) => {
    setExistingFotoUrls((prev) => prev.filter((_, index) => index !== indexToRemove));
  }, []);

  const removeNewFotoById = useCallback((fotoId) => {
    setNewFotoBuktiEntries((prev) => prev.filter((entry) => entry.id !== fotoId));
  }, []);

  const resetFotoSelection = useCallback(({ preserveExisting = false } = {}) => {
    setNewFotoBuktiEntries([]);
    if (!preserveExisting) {
      setExistingFotoUrls([]);
    }
  }, []);

  const currentFotoCount = existingFotoUrls.length + newFotoBuktiEntries.length;

  const fotoGalleryItems = useMemo(
    () =>
      createProofGalleryItems({
        existingUrls: existingFotoUrls,
        newEntries: newFotoBuktiEntries,
        onRemoveExisting: removeExistingFotoAt,
        onRemoveNew: removeNewFotoById,
      }),
    [existingFotoUrls, newFotoBuktiEntries, removeExistingFotoAt, removeNewFotoById]
  );

  const fetchTransaksi = useCallback(
    async ({ append = false, baseOffset = 0, filterStartDate, filterEndDate, filterSearchTerm = "" } = {}) => {
      if (append && transaksiFetchInFlightRef.current) {
        return;
      }

      const nextOffset = append ? baseOffset : 0;
      const startDateParam =
        typeof filterStartDate === "string"
          ? filterStartDate
          : historyDateFilter.startDate;
      const endDateParam =
        typeof filterEndDate === "string"
          ? filterEndDate
          : historyDateFilter.endDate;
      const searchTermParam =
        typeof filterSearchTerm === "string" ? filterSearchTerm : "";

      transaksiFetchInFlightRef.current = true;
      setLoadingMoreTransaksi(true);
      try {
        const transaksiRes = await axios.get(`${API}/barang-keluar`, {
          params: {
            limit: HISTORY_PAGE_SIZE,
            offset: nextOffset,
            start_date: startDateParam || undefined,
            end_date: endDateParam || undefined,
            search: searchTermParam.trim() || undefined,
          },
        });
        const rows = transaksiRes.data || [];
        const totalFromHeader = Number(transaksiRes.headers?.["x-total-count"]);
        setTransaksi((prev) => {
          const mergedRows = append ? [...prev, ...rows] : rows;
          const seenIds = new Set();
          return mergedRows.filter((row) => {
            const transaksiId = row?.keluar_id;
            if (!transaksiId) {
              return true;
            }
            if (seenIds.has(transaksiId)) {
              return false;
            }
            seenIds.add(transaksiId);
            return true;
          });
        });
        const updatedOffset = nextOffset + rows.length;
        setTransaksiOffset(updatedOffset);
        if (Number.isFinite(totalFromHeader)) {
          setTotalTransaksi(totalFromHeader);
          setHasMoreTransaksi(updatedOffset < totalFromHeader);
        } else {
          setHasMoreTransaksi(rows.length === HISTORY_PAGE_SIZE);
        }
      } catch (error) {
        toast.error("Gagal memuat riwayat barang keluar");
      } finally {
        transaksiFetchInFlightRef.current = false;
        setLoadingMoreTransaksi(false);
      }
    },
    [historyDateFilter.endDate, historyDateFilter.startDate]
  );

  const fetchData = useCallback(async () => {
    try {
      const barangsRes = await axios.get(`${API}/barang`);
      setBarangs(barangsRes.data.filter((b) => b.aktif !== false));
      if (canViewHistory) {
        await fetchTransaksi({ append: false, baseOffset: 0, filterSearchTerm: "" });
      } else {
        setTransaksi([]);
        setTransaksiOffset(0);
        setTotalTransaksi(0);
        setHasMoreTransaksi(false);
      }
    } catch (error) {
      setBarangs([]);
      toast.error("Gagal memuat daftar barang");
    } finally {
      setLoading(false);
    }
  }, [canViewHistory, fetchTransaksi]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApplyHistoryDateFilter = () => {
    if (
      historyDateDraft.startDate &&
      historyDateDraft.endDate &&
      historyDateDraft.startDate > historyDateDraft.endDate
    ) {
      toast.error("Tanggal mulai tidak boleh lebih besar dari tanggal akhir");
      return;
    }

    const nextFilter = {
      startDate: historyDateDraft.startDate,
      endDate: historyDateDraft.endDate,
    };

    setHistoryDateFilter(nextFilter);
    fetchTransaksi({
      append: false,
      baseOffset: 0,
      filterStartDate: nextFilter.startDate,
      filterEndDate: nextFilter.endDate,
      filterSearchTerm: historySearchTerm,
    });
  };

  const handleResetHistoryDateFilter = () => {
    const emptyFilter = { startDate: "", endDate: "" };
    setHistoryDateDraft(emptyFilter);
    setHistoryDateFilter(emptyFilter);
    setHistorySearchTerm("");
    fetchTransaksi({
      append: false,
      baseOffset: 0,
      filterStartDate: "",
      filterEndDate: "",
      filterSearchTerm: "",
    });
  };

  const isHistoryFilterActive = Boolean(
    historyDateFilter.startDate || historyDateFilter.endDate
  );

  const handleAddItem = () => {
    const qty = parseFloat(newItem.qty);
    const nextItemErrors = {};

    if (!newItem.barang_id) {
      nextItemErrors.barang_id = "Pilih barang terlebih dahulu.";
    }
    if (!qty || qty <= 0) {
      nextItemErrors.qty = "Qty keluar harus lebih dari 0.";
    }

    const barang = barangs.find((b) => b.barang_id === newItem.barang_id);
    const satuanInput = newItem.satuan_input || barang?.satuan || "";
    if (!satuanInput) {
      nextItemErrors.satuan_input = "Pilih satuan qty terlebih dahulu.";
    }

    if (Object.keys(nextItemErrors).length > 0) {
      setNewItemErrors(nextItemErrors);
      return;
    }

    const faktor = Number(
      barang?.konversi_satuan?.[String(satuanInput).toLowerCase()] ??
        (String(satuanInput).toLowerCase() === String(barang?.satuan).toLowerCase() ? 1 : NaN)
    );
    if (!Number.isFinite(faktor) || faktor <= 0) {
      setNewItemErrors({ satuan_input: "Satuan item belum terkonfigurasi di master barang." });
      return;
    }
    const qtyKonversi = qty * faktor;

    const baselineQty = Number(editBaselineQtyMap[newItem.barang_id] || 0);
    const otherQty = formData.items
      .filter((i) => i.barang_id === newItem.barang_id)
      .reduce((sum, i) => sum + Number(i.qty || 0), 0);
    const maxAllowedQty = Number(barang?.stok_current || 0) + baselineQty;
    if (otherQty + qtyKonversi > maxAllowedQty) {
      setNewItemErrors({ qty: `Stok ${barang.nama_barang} tidak cukup untuk qty yang diminta.` });
      return;
    }

    setFormData({
      ...formData,
      items: [...formData.items, { ...newItem, qty: qtyKonversi, qty_input: qty, satuan_input: satuanInput }],
    });
    setFormErrors((prev) => ({ ...prev, items: "" }));
    setNewItemErrors({});

    setNewItem({
      barang_id: "",
      qty: "",
      satuan_input: "",
      keterangan: "",
    });
    setSearchBarang("");
    requestAnimationFrame(() => {
      barangInputRef.current?.focus();
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
    if (editingItemIndex === index) {
      handleCancelEditItem();
    }
  };

  const handleStartEditItem = (item, index) => {
    setEditingItemIndex(index);
    setEditingItemDraft({
      qty_input: String(item.qty_input ?? item.qty ?? ""),
      satuan_input: item.satuan_input || "",
      keterangan: item.keterangan || "",
    });
    focusFieldNextFrame(editQtyInputRef);
  };

  const handleCancelEditItem = () => {
    setEditingItemIndex(null);
    setEditingItemDraft({ qty_input: "", satuan_input: "", keterangan: "" });
    focusFieldNextFrame(barangInputRef);
  };

  const handleSaveEditItem = (index) => {
    const currentItem = formData.items[index];
    const barang = barangs.find((b) => b.barang_id === currentItem?.barang_id);
    if (!barang) {
      toast.error("Barang tidak ditemukan");
      return;
    }

    const qtyInput = parseFloat(editingItemDraft.qty_input);
    if (!qtyInput || qtyInput <= 0) {
      toast.error("Masukkan qty yang valid");
      return;
    }

    const satuanInput = editingItemDraft.satuan_input || barang.satuan || "";
    const faktor = Number(
      barang?.konversi_satuan?.[String(satuanInput).toLowerCase()] ??
        (String(satuanInput).toLowerCase() === String(barang?.satuan).toLowerCase() ? 1 : NaN)
    );
    if (!Number.isFinite(faktor) || faktor <= 0) {
      toast.error("Satuan item belum terkonfigurasi");
      return;
    }

    const qtyKonversi = qtyInput * faktor;
    const baselineQty = Number(editBaselineQtyMap[currentItem.barang_id] || 0);
    const otherQty = formData.items.reduce((sum, item, i) => {
      if (i === index || item.barang_id !== currentItem.barang_id) {
        return sum;
      }
      return sum + Number(item.qty || 0);
    }, 0);
    const maxAllowedQty = Number(barang.stok_current || 0) + baselineQty;
    if (otherQty + qtyKonversi > maxAllowedQty) {
      toast.error(`Stok ${barang.nama_barang} tidak cukup`);
      return;
    }

    const updatedItems = formData.items.map((item, i) =>
      i === index
        ? {
            ...item,
            qty: qtyKonversi,
            qty_input: qtyInput,
            satuan_input: satuanInput,
            keterangan: editingItemDraft.keterangan,
          }
        : item
    );

    setFormData({ ...formData, items: updatedItems });
    handleCancelEditItem();
  };

  const executeSubmit = async () => {
    if (isSubmitting || submitInFlightRef.current) {
      return;
    }
    if (formData.items.length === 0) {
      setFormErrors((prev) => ({ ...prev, items: "Tambahkan minimal 1 item sebelum menyimpan transaksi." }));
      return;
    }
    if (currentFotoCount > MAX_TRANSACTION_PROOF_IMAGES) {
      toast.error(`Maksimal ${MAX_TRANSACTION_PROOF_IMAGES} foto bukti per transaksi`);
      return;
    }

    submitInFlightRef.current = true;
    setIsSubmitting(true);
    try {
      const uploadedFotoUrls = [];
      for (const fotoEntry of newFotoBuktiEntries) {
        const fotoFormData = new FormData();
        fotoFormData.append("file", fotoEntry.file);
        const uploadRes = await axios.post(`${API}/upload/barang-keluar`, fotoFormData);
        uploadedFotoUrls.push(uploadRes.data.url);
      }

      const fotoUrls = [...existingFotoUrls, ...uploadedFotoUrls];

      const submitData = new FormData();
      submitData.append("items", JSON.stringify(formData.items));
      submitData.append("keterangan", formData.keterangan || "");
      submitData.append("foto_bukti_list", JSON.stringify(fotoUrls));
      submitData.append("tanggal", selectedDate);

      if (editMode && editingKeluarId) {
        await axios.put(`${API}/barang-keluar/${editingKeluarId}`, submitData);
        toast.success("Transaksi barang keluar berhasil diupdate");
      } else {
        await axios.post(`${API}/barang-keluar`, submitData);
        toast.success("Barang keluar berhasil dicatat");
      }
      setFormData({
        keterangan: "",
        items: [],
      });
      setEditMode(false);
      setEditingKeluarId(null);
      setEditBaselineQtyMap({});
      handleCancelEditItem();
      setSelectedDate(getTodayInBusinessTz());
      if (user?.username) {
        localStorage.removeItem(draftKey);
      }
      resetFotoSelection();
      setSubmitConfirmOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal menyimpan data");
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      setFormErrors((prev) => ({ ...prev, items: "Tambahkan minimal 1 item sebelum menyimpan transaksi." }));
      return;
    }
    setSubmitConfirmOpen(true);
  };

  const handleFotoFilesSelected = async (files) => {
    const selectedFiles = Array.from(files || []);

    if (selectedFiles.length === 0) {
      return;
    }

    const imageFiles = selectedFiles.filter(isLikelyProofImageFile);
    if (imageFiles.length === 0) {
      toast.error("Semua file foto bukti harus berupa gambar");
      return;
    }

    if (imageFiles.length < selectedFiles.length) {
      toast.warning("Beberapa file diabaikan karena bukan gambar");
    }

    const validSizeFiles = imageFiles.filter(
      (file) => Number(file.size || 0) <= MAX_TRANSACTION_PROOF_IMAGE_BYTES
    );
    if (validSizeFiles.length === 0) {
      toast.error("Ukuran setiap foto maksimal 5MB");
      return;
    }

    if (validSizeFiles.length < imageFiles.length) {
      toast.warning("Beberapa foto diabaikan karena melebihi 5MB");
    }

    const remainingSlots = MAX_TRANSACTION_PROOF_IMAGES - currentFotoCount;
    if (remainingSlots <= 0) {
      toast.error(`Maksimal ${MAX_TRANSACTION_PROOF_IMAGES} foto bukti per transaksi`);
      return;
    }

    const nextFiles = validSizeFiles.slice(0, remainingSlots);
    if (validSizeFiles.length > remainingSlots) {
      toast.warning(`Hanya ${remainingSlots} foto yang ditambahkan karena batas maksimal ${MAX_TRANSACTION_PROOF_IMAGES} foto.`);
    }

    try {
      const nextEntries = await createLocalProofImageEntries(nextFiles, "barang-keluar");
      setNewFotoBuktiEntries((prev) => [...prev, ...nextEntries]);
    } catch {
      toast.error("Gagal menyiapkan preview foto bukti");
    }
  };

  const getBarangName = useCallback((barangId) => {
    const barang = barangs.find((b) => b.barang_id === barangId);
    return barang ? barang.nama_barang : "-";
  }, [barangs]);

  const getBarangSatuan = (barangId) => {
    const barang = barangs.find((b) => b.barang_id === barangId);
    return barang ? barang.satuan : "";
  };

  const getItemQtyWithSatuan = (item) => {
    const qtyDisplay = item?.qty_input ?? item?.qty ?? 0;
    const satuanDisplay = item?.satuan_input || getBarangSatuan(item?.barang_id) || "-";
    return `${qtyDisplay} ${satuanDisplay}`;
  };

  const getBarangStok = (barangId) => {
    const barang = barangs.find((b) => b.barang_id === barangId);
    return barang ? barang.stok_current : 0;
  };

  const filteredHistoryTransaksi = useMemo(() => {
    const keyword = historySearchTerm.trim().toLowerCase();
    if (!keyword) {
      return transaksi;
    }

    return transaksi.filter((entry) => {
      const searchableParts = [
        entry?.petugas,
        entry?.keterangan,
        ...(entry?.items || []).map((item) => getBarangName(item?.barang_id)),
        ...(entry?.items || []).map((item) => item?.keterangan),
      ];

      return searchableParts
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(keyword);
    });
  }, [getBarangName, historySearchTerm, transaksi]);

  const isHistorySearchActive = Boolean(isHistoryFilterActive || historySearchTerm.trim());

  const handleViewDetail = (t) => {
    setSelectedTransaksi(t);
    setDetailDialog(true);
  };

  const handleEditTransaksi = (t) => {
    setEditMode(true);
    setEditingKeluarId(t.keluar_id);
    setSelectedDate(String(t.tanggal).slice(0, 10));
    setFormData({
      keterangan: t.keterangan || "",
      items: (t.items || []).map((item) => ({
        barang_id: item.barang_id,
        qty: Number(item.qty),
        qty_input: Number(item.qty_input ?? item.qty),
        satuan_input: item.satuan_input || "",
        keterangan: item.keterangan || "",
      })),
    });
    setEditBaselineQtyMap(sumQtyByBarangId(t.items || []));
    setExistingFotoUrls(getTransactionProofImageUrls(t));
    setNewFotoBuktiEntries([]);
    handleCancelEditItem();
  };

  const handleDeleteTransaksi = async (keluarId) => {
    setConfirmDialog({
      open: true,
      title: "Konfirmasi Hapus Transaksi",
      description: "Apakah Anda yakin ingin menghapus transaksi ini? Tindakan ini tidak dapat dibatalkan.",
      confirmText: "Ya, Hapus",
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/barang-keluar/${keluarId}`);
          toast.success("Transaksi barang keluar berhasil dihapus");
          if (editMode && editingKeluarId === keluarId) {
            handleCancelEdit();
          }
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Gagal menghapus transaksi");
        }
      },
    });
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditingKeluarId(null);
    setEditBaselineQtyMap({});
    handleCancelEditItem();
    setFormData({
      keterangan: "",
      items: [],
    });
    setSelectedDate(getTodayInBusinessTz());
    resetFotoSelection();
  };

  const selectedBarang = barangs.find((b) => b.barang_id === newItem.barang_id);
  const selectedBarangOutOfStock = selectedBarang
    ? selectedBarang.stok_current <= 0
    : false;
  const filteredBarangs = barangs.filter((b) => {
    const namaBarang = typeof b.nama_barang === "string" ? b.nama_barang : "";
    return (
      namaBarang.toLowerCase().includes(searchBarang.toLowerCase()) ||
      searchBarang === ""
    );
  });
  const selectedTransaksiFotoGalleryItems = useMemo(
    () =>
      getTransactionProofImageUrls(selectedTransaksi).map((url, index) => {
        const imageSrc = buildProofImageSrc(url);
        return {
          id: `${selectedTransaksi?.keluar_id || "keluar"}-${index}`,
          src: imageSrc,
          href: imageSrc,
          alt: `Foto bukti transaksi ${index + 1}`,
          label: `Foto ${index + 1}`,
        };
      }),
    [selectedTransaksi]
  );

  if (loading) {
    return <PageLoadingState label="Memuat transaksi barang keluar" description="Menyiapkan daftar barang dan riwayat pemakaian terbaru." />;
  }

  return (
    <PageShell data-testid="barang-keluar-page">
      <PageHero
        tone="amber"
        eyebrow="Operasional Pemakaian"
        title="Transaksi Barang Keluar Dapur"
        description="Catat distribusi dan penggunaan barang agar stok dapur selalu terkontrol dengan struktur halaman yang lebih mudah dipindai."
        actions={(
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/dashboard")}
            className="shrink-0"
          >
            <Home size={16} />
            Dashboard
          </Button>
        )}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-6 shadow-sm">
          <h2 className="font-manrope font-extrabold text-2xl md:text-[1.75rem] mb-6 text-amber-900">
            {editMode ? "Edit Transaksi Barang Keluar" : "Form Pengeluaran Barang"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label>Tanggal Transaksi</Label>
              <Input
                data-testid="input-tanggal-keluar"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={getTodayInBusinessTz()}
              />
            </div>

            <div className="space-y-2">
              <Label>Catatan Pengeluaran (Opsional)</Label>
              <Textarea
                data-testid="input-keterangan"
                value={formData.keterangan}
                onChange={(e) =>
                  setFormData({ ...formData, keterangan: e.target.value })
                }
                placeholder="Nabella (shift pagi)"
                rows={3}
              />
              <p className={getFieldMessageClass()}>
                Username login tetap tercatat otomatis sebagai petugas. Gunakan kolom ini untuk shift, lokasi, atau catatan tambahan.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Foto Bukti (Opsional)</Label>
              <ProofImageDropzone
                inputTestId="input-foto-bukti-keluar"
                accept={PROOF_IMAGE_ACCEPT}
                multiple
                tone="amber"
                currentCount={currentFotoCount}
                maxCount={MAX_TRANSACTION_PROOF_IMAGES}
                onFilesSelected={handleFotoFilesSelected}
              />
              <p className={getFieldMessageClass()}>
                Upload sampai {MAX_TRANSACTION_PROOF_IMAGES} foto. Setiap file maksimal 5MB dan otomatis dikompres saat disimpan.
              </p>
              {currentFotoCount > 0 ? (
                <ProofImageGallery
                  title={`Foto Bukti (${currentFotoCount}/${MAX_TRANSACTION_PROOF_IMAGES})`}
                  hint="Klik gambar untuk membuka ukuran penuh. Anda bisa menghapus foto satu per satu sebelum menyimpan."
                  images={fotoGalleryItems}
                />
              ) : null}
            </div>

            <div className="border-t border-amber-200/80 pt-6">
              <h3 className="font-manrope font-bold text-xl md:text-2xl mb-4">Tambah Item</h3>
              <InlineHint className="border-amber-200/80 bg-white/70">
                Pilih barang, tentukan qty dan satuan, lalu tambahkan ke daftar. Anda tetap bisa meninjau detail sebelum menyimpan transaksi.
              </InlineHint>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Barang</Label>
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      Barang tersedia: {filteredBarangs.length}
                    </p>
                    <SearchableDropdown
                      value={newItem.barang_id}
                      searchValue={searchBarang}
                      onSearchValueChange={setSearchBarang}
                      onValueChange={(value) => {
                        const barang = barangs.find((b) => b.barang_id === value);
                        setNewItem({ ...newItem, barang_id: value, satuan_input: barang?.satuan || "" });
                        setNewItemErrors((prev) => ({ ...prev, barang_id: "", satuan_input: "", qty: "" }));
                        setSearchBarang("");
                        focusFieldNextFrame(qtyInputRef);
                      }}
                      error={newItemErrors.barang_id}
                      helperText="Ketik nama barang untuk memfilter stok yang tersedia."
                      selectedLabel="Barang terpilih"
                      selectedPlaceholder="Pilih barang"
                      emptyText="Tidak ada barang tersedia"
                      inputTestId="search-barang-keluar"
                      listTestId="select-barang-keluar"
                      inputRef={barangInputRef}
                      items={filteredBarangs.map((barang) => ({
                        value: barang.barang_id,
                        label: `${barang.nama_barang || "Tanpa nama"} - Stok: ${formatStockValue(barang.stok_current)} ${barang.satuan || "-"}${barang.stok_current <= 0 ? " (Stok habis)" : ""}`,
                        searchText: barang.nama_barang || "",
                        disabled: barang.stok_current <= 0,
                      }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Qty Keluar</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      ref={qtyInputRef}
                      data-testid="input-qty-keluar"
                      type="number"
                      placeholder="Masukkan qty"
                      value={newItem.qty}
                      onChange={(e) =>
                        {
                          setNewItem({
                            ...newItem,
                            qty: e.target.value,
                          });
                          setNewItemErrors((prev) => ({ ...prev, qty: "" }));
                        }
                      }
                      onKeyDown={(event) =>
                        handleArrowFieldKeyDown(event, {
                          onEnter: handleAddItem,
                          onLeft: () => barangInputRef.current?.focus(),
                          onRight: () => satuanSelectRef.current?.focus(),
                        })
                      }
                      className={getFieldErrorClass(newItemErrors.qty)}
                      aria-invalid={Boolean(newItemErrors.qty)}
                    />
                    <Select
                      value={newItem.satuan_input}
                      onValueChange={(value) =>
                        {
                          setNewItem({ ...newItem, satuan_input: value });
                          setNewItemErrors((prev) => ({ ...prev, satuan_input: "" }));
                        }
                      }
                      disabled={!newItem.barang_id}
                    >
                      <SelectTrigger
                        ref={satuanSelectRef}
                        className={getFieldErrorClass(newItemErrors.satuan_input)}
                        aria-invalid={Boolean(newItemErrors.satuan_input)}
                        onKeyDown={(event) =>
                          handleArrowFieldKeyDown(event, {
                            onLeft: () => qtyInputRef.current?.focus(),
                            onRight: () => keteranganInputRef.current?.focus(),
                          })
                        }
                      >
                        <SelectValue placeholder="Satuan" />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          const selected = barangs.find((b) => b.barang_id === newItem.barang_id);
                          return getBarangUnits(selected).map((unit) => (
                            <SelectItem key={unit} value={unit}>
                              {unit}
                            </SelectItem>
                          ));
                        })()}
                      </SelectContent>
                    </Select>
                  </div>
                    {newItem.barang_id && (
                      <p className="text-sm text-muted-foreground">
                        Stok tersedia: {formatStockValue(getBarangStok(newItem.barang_id))}
                      </p>
                    )}
                    {newItemErrors.qty ? <p className={getFieldMessageClass(true)}>{newItemErrors.qty}</p> : null}
                    {newItemErrors.satuan_input ? <p className={getFieldMessageClass(true)}>{newItemErrors.satuan_input}</p> : null}
                    {selectedBarangOutOfStock && (
                      <p className="text-sm text-destructive">
                        Barang ini stoknya habis dan tidak bisa ditambahkan.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Keterangan Item (Opsional)</Label>
                    <Input
                      ref={keteranganInputRef}
                      data-testid="input-keterangan-item"
                      value={newItem.keterangan}
                      onChange={(e) =>
                        setNewItem({ ...newItem, keterangan: e.target.value })
                      }
                      placeholder="Detail penggunaan"
                      onKeyDown={(event) =>
                        handleArrowFieldKeyDown(event, {
                          onEnter: handleAddItem,
                          onLeft: () => satuanSelectRef.current?.focus(),
                          onRight: () => addItemButtonRef.current?.focus(),
                        })
                      }
                    />
                </div>

                <Button
                  ref={addItemButtonRef}
                  data-testid="add-item-keluar-button"
                  type="button"
                  variant="outline"
                  onKeyDown={(event) =>
                    handleArrowFieldKeyDown(event, {
                      onLeft: () => keteranganInputRef.current?.focus(),
                    })
                  }
                  onClick={handleAddItem}
                  className="w-full"
                  disabled={selectedBarangOutOfStock}
                >
                  <Plus size={16} className="mr-2" />
                  Tambah Item
                </Button>
                {formErrors.items ? <p className={getFieldMessageClass(true)}>{formErrors.items}</p> : null}
              </div>
            </div>

            {formData.items.length > 0 && (
              <div className="border-t border-amber-200/80 pt-6">
                <h3 className="font-manrope font-bold text-xl md:text-2xl mb-4">Daftar Item</h3>
                <div className="space-y-2">
                  {formData.items.map((item, index) => (
                    <div
                      key={index}
                      data-testid={`item-keluar-${index}`}
                      className={`flex items-center justify-between p-3 border rounded-md transition-colors ${
                        editingItemIndex === index
                          ? "border-primary bg-primary/5"
                          : ""
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-xs font-manrope font-bold text-amber-800">
                            {index + 1}
                          </span>
                          <p className="font-medium">
                            {getBarangName(item.barang_id)}
                          </p>
                        </div>
                        {editingItemIndex === index && (
                          <p className="text-xs text-primary font-medium mt-1">Sedang diedit</p>
                        )}
                        {editingItemIndex === index ? (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                            <Input
                              ref={editQtyInputRef}
                              type="number"
                              min="0"
                              step="any"
                              value={editingItemDraft.qty_input}
                              onChange={(e) =>
                                setEditingItemDraft({
                                  ...editingItemDraft,
                                  qty_input: e.target.value,
                                })
                              }
                              placeholder="Qty"
                              onKeyDown={(event) =>
                                handleArrowFieldKeyDown(event, {
                                  onEnter: () => editSatuanSelectRef.current?.focus(),
                                  onRight: () => editSatuanSelectRef.current?.focus(),
                                })
                              }
                            />
                            <Select
                              value={editingItemDraft.satuan_input}
                              onValueChange={(value) =>
                                {
                                  setEditingItemDraft({
                                    ...editingItemDraft,
                                    satuan_input: value,
                                  });
                                }
                              }
                            >
                              <SelectTrigger
                                ref={editSatuanSelectRef}
                                onKeyDown={(event) =>
                                  handleArrowFieldKeyDown(event, {
                                    onLeft: () => editQtyInputRef.current?.focus(),
                                    onRight: () => editKeteranganInputRef.current?.focus(),
                                  })
                                }
                              >
                                <SelectValue placeholder="Satuan" />
                              </SelectTrigger>
                              <SelectContent>
                                {getBarangUnits(
                                  barangs.find((b) => b.barang_id === item.barang_id)
                                ).map((unit) => (
                                  <SelectItem key={unit} value={unit}>
                                    {unit}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                              <Input
                                ref={editKeteranganInputRef}
                                value={editingItemDraft.keterangan}
                                onChange={(e) =>
                                  setEditingItemDraft({
                                    ...editingItemDraft,
                                    keterangan: e.target.value,
                                  })
                                }
                                placeholder="Keterangan"
                                onKeyDown={(event) =>
                                  handleArrowFieldKeyDown(event, {
                                    onEnter: () => handleSaveEditItem(index),
                                    onLeft: () => editSatuanSelectRef.current?.focus(),
                                    onRight: () => editSaveButtonRef.current?.focus(),
                                  })
                                }
                              />
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            Qty: {getItemQtyWithSatuan(item)}
                            {item.keterangan && ` - ${item.keterangan}`}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {editingItemIndex === index ? (
                          <>
                            <Button
                              ref={editSaveButtonRef}
                              type="button"
                              size="sm"
                              onKeyDown={(event) =>
                                handleArrowFieldKeyDown(event, {
                                  onLeft: () => editKeteranganInputRef.current?.focus(),
                                })
                              }
                              onClick={() => handleSaveEditItem(index)}
                            >
                              Simpan
                            </Button>
                            <Button type="button" size="sm" variant="outline" onClick={handleCancelEditItem}>
                              Batal
                            </Button>
                          </>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleStartEditItem(item, index)}
                          >
                            <Pencil size={16} className="mr-1" />
                            Ubah
                          </Button>
                        )}
                        <Button
                          data-testid={`remove-item-keluar-${index}`}
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveItem(index)}
                        >
                          <Trash2 size={16} className="text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button
              data-testid="submit-barang-keluar"
              type="submit"
              className="w-full"
              disabled={!canCreateBarangKeluar || formData.items.length === 0 || isSubmitting}
            >
              <Save size={16} className="mr-2" />
              {editMode ? "Update Transaksi" : "Simpan Transaksi"}
            </Button>
            {editMode && (
              <Button type="button" variant="outline" className="w-full" onClick={handleCancelEdit}>
                Batal Edit
              </Button>
            )}
          </form>
        </div>

        {canViewHistory && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-6 shadow-sm">
            <h2 className="font-manrope font-extrabold text-2xl md:text-[1.75rem] mb-6 text-slate-900">
              Riwayat Barang Keluar
            </h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 mb-3">
              <div>
                <Label>Tanggal mulai</Label>
                <Input
                  type="date"
                  value={historyDateDraft.startDate}
                  onChange={(e) =>
                    setHistoryDateDraft((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <Label>Tanggal akhir</Label>
                <Input
                  type="date"
                  value={historyDateDraft.endDate}
                  onChange={(e) =>
                    setHistoryDateDraft((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="xl:col-span-1">
                <Label>Cari riwayat</Label>
                <Input
                  type="text"
                  value={historySearchTerm}
                  onChange={(e) => setHistorySearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleApplyHistoryDateFilter();
                    }
                  }}
                  placeholder="Cari petugas, catatan, atau nama barang"
                />
              </div>
            </div>
            <div className="mb-4 flex flex-wrap gap-2">
              <Button type="button" size="sm" variant="outline" onClick={handleApplyHistoryDateFilter}>
                Terapkan Filter
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleResetHistoryDateFilter}
                disabled={
                  !isHistorySearchActive &&
                  !historyDateDraft.startDate &&
                  !historyDateDraft.endDate &&
                  !historySearchTerm
                }
              >
                Reset Semua
              </Button>
            </div>
            {transaksi.length > 0 && (
              <p className="mb-3 text-sm text-muted-foreground">
                Menampilkan {filteredHistoryTransaksi.length} hasil dari {transaksi.length} data dimuat{totalTransaksi > 0 ? ` dari total ${totalTransaksi}` : ""}
              </p>
            )}
            {isHistorySearchActive && transaksi.length > 0 && filteredHistoryTransaksi.length === 0 && hasMoreTransaksi && (
              <p className="mb-3 text-sm text-muted-foreground">
                Belum ada hasil pada data yang sudah dimuat. Muat data tambahan atau ubah kata kunci pencarian.
              </p>
            )}
            <div className="max-h-[600px] space-y-4 overflow-y-auto">
              {filteredHistoryTransaksi.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">
                  {transaksi.length === 0
                    ? isHistoryFilterActive
                      ? "Tidak ada transaksi pada rentang tanggal ini"
                      : "Belum ada transaksi"
                    : "Tidak ada hasil yang cocok dengan pencarian ini"}
                </p>
              ) : (
                filteredHistoryTransaksi.map((t) => (
                  <div
                    key={t.keluar_id}
                    data-testid={`transaksi-keluar-${t.keluar_id}`}
                    className="rounded-md border p-4 transition-colors hover:bg-muted/50"
                  >
                    <div className="mb-2 flex items-start justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {new Date(t.tanggal).toLocaleDateString("id-ID", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                        <p className="text-sm font-medium">Petugas: {t.petugas}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {canManageHistory && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditTransaksi(t)}
                          >
                            <Pencil size={16} className="mr-1" />
                            Edit
                          </Button>
                        )}
                        {canManageHistory && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteTransaksi(t.keluar_id)}
                          >
                            <Trash2 size={16} className="mr-1" />
                            Hapus
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDetail(t)}
                        >
                          <Eye size={16} className="mr-1" />
                          Detail
                        </Button>
                      </div>
                    </div>
                    {t.keterangan && (
                      <p className="mb-2 text-sm text-muted-foreground">
                        {t.keterangan}
                      </p>
                    )}
                    <div className="space-y-1">
                      {t.items.map((item, idx) => (
                        <p key={idx} className="text-sm">
                          {idx + 1}. {getBarangName(item.barang_id)} ({getItemQtyWithSatuan(item)})
                          {item.keterangan && ` - ${item.keterangan}`}
                        </p>
                      ))}
                    </div>
                  </div>
                ))
              )}
              {transaksi.length > 0 && (
                <div className="pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={!hasMoreTransaksi || loadingMoreTransaksi}
                    onClick={() =>
                      fetchTransaksi({
                        append: true,
                        baseOffset: transaksiOffset,
                        filterSearchTerm: historySearchTerm,
                      })
                    }
                  >
                    {loadingMoreTransaksi
                      ? "Memuat..."
                      : hasMoreTransaksi
                        ? "Muat Lebih Banyak"
                        : "Semua data sudah ditampilkan"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detail Barang Keluar</DialogTitle>
          </DialogHeader>
          {selectedTransaksi && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Tanggal</p>
                  <p className="font-medium">
                    {new Date(selectedTransaksi.tanggal).toLocaleDateString(
                      "id-ID",
                      {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Petugas</p>
                  <p className="font-medium">{selectedTransaksi.petugas}</p>
                </div>
                {selectedTransaksi.keterangan && (
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground">Keterangan</p>
                    <p className="font-medium">{selectedTransaksi.keterangan}</p>
                  </div>
                )}
              </div>

              {selectedTransaksiFotoGalleryItems.length > 0 ? (
                <ProofImageGallery
                  title={`Foto Bukti (${selectedTransaksiFotoGalleryItems.length})`}
                  hint="Klik gambar untuk membuka ukuran penuh. Semua bukti ditampilkan dengan ukuran thumbnail yang konsisten."
                  images={selectedTransaksiFotoGalleryItems}
                />
              ) : null}

              <div>
                <p className="text-sm font-manrope font-semibold mb-2">
                  Daftar Item ({selectedTransaksi.items.length})
                </p>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-center w-14">No</th>
                        <th className="px-3 py-2 text-left">Barang</th>
                        <th className="px-3 py-2 text-center">Qty</th>
                        <th className="px-3 py-2 text-left">Keterangan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTransaksi.items.map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-3 py-2 text-center font-medium">{idx + 1}</td>
                          <td className="px-3 py-2">
                            {getBarangName(item.barang_id)}
                          </td>
                          <td className="px-3 py-2 text-center">{getItemQtyWithSatuan(item)}</td>
                          <td className="px-3 py-2">
                            {item.keterangan || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={submitConfirmOpen} onOpenChange={setSubmitConfirmOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editMode ? "Konfirmasi Perbarui Barang Keluar" : "Konfirmasi Simpan Barang Keluar"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className={getFieldMessageClass()}>
              Tinjau ringkasan {formData.items.length} item berikut sebelum transaksi barang keluar diproses.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <p className="text-muted-foreground">Tanggal</p>
                <p className="font-medium">{selectedDate}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Jumlah Item</p>
                <p className="font-manrope font-bold">{formData.items.length}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Total Qty Dasar</p>
                <p className="font-manrope font-bold text-primary">
                  {formData.items.reduce((sum, item) => sum + Number(item.qty || 0), 0).toLocaleString("id-ID")}
                </p>
              </div>
            </div>
            {formData.keterangan && (
              <div>
                <p className="text-muted-foreground">Keterangan</p>
                <p className="font-medium">{formData.keterangan}</p>
              </div>
            )}
            {currentFotoCount > 0 ? (
              <ProofImageGallery
                title={`Preview Foto Bukti (${currentFotoCount}/${MAX_TRANSACTION_PROOF_IMAGES})`}
                hint="Klik gambar untuk membuka ukuran penuh. Foto yang dihapus dari sini tidak akan ikut tersimpan."
                images={fotoGalleryItems}
              />
            ) : null}
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-center w-14">No</th>
                    <th className="px-3 py-2 text-left">Barang</th>
                    <th className="px-3 py-2 text-center">Qty Input</th>
                    <th className="px-3 py-2 text-center">Qty Dasar</th>
                    <th className="px-3 py-2 text-left">Keterangan</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2 text-center font-medium">{idx + 1}</td>
                      <td className="px-3 py-2">{getBarangName(item.barang_id)}</td>
                      <td className="px-3 py-2 text-center">{item.qty_input ?? item.qty} {item.satuan_input || ""}</td>
                      <td className="px-3 py-2 text-center">{item.qty}</td>
                      <td className="px-3 py-2">{item.keterangan || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setSubmitConfirmOpen(false)} disabled={isSubmitting}>
                Batal
              </Button>
              <Button type="button" onClick={executeSubmit} disabled={isSubmitting}>
                Ya, {editMode ? "Perbarui" : "Simpan"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmActionDialog
        open={confirmDialog.open}
        onOpenChange={(open) =>
          setConfirmDialog((prev) => ({ ...prev, open, onConfirm: open ? prev.onConfirm : null }))
        }
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        destructive={confirmDialog.destructive}
        onConfirm={async () => {
          if (confirmDialog.onConfirm) {
            await confirmDialog.onConfirm();
          }
          setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
        }}
      />
    </PageShell>
  );
};

export default BarangKeluarPage;
